import type { AxiosInstance } from 'axios';
import { normalizeAxiosError } from '../utils/apiError';
import type { StkRequestSnapshot } from '../utils/mpesaStkPrompt';
import type {
  MatchMpesaPaymentPayload,
  MpesaStkPushPayload,
} from '../utils/mpesaOrderPayment';

const STK_REQUEST_STATUS_ENDPOINT =
  'bp_get_mpesa_stk_request_status_information';
const ORDER_PAYMENT_STREAM_ENDPOINT = 'bp_order_payment_stream';

/** STK push response body (200). `success: false` means the prompt was not sent. */
export interface MpesaStkPushResponse {
  status: number;
  success?: boolean;
  message?: string;
  bp_stk_request_id?: string | null;
  bp_stk_status?: string;
  bp_charge_amount?: number;
  data?: { payment?: Record<string, any>; order_status?: string };
}

/** 409 body when a prompt for the order is still open (STK_ALREADY_PENDING). */
export interface MpesaStkAlreadyPendingBody {
  status: 409;
  code: 'STK_ALREADY_PENDING';
  message: string;
  bp_stk_request_id?: string;
}

// Core Mpesa API functions (raw HTTP only)
const getUnapprovedMpesaPaymentsCore = async (
  axiosinstance: AxiosInstance,
  page: number,
  limit: number,
  auth: { accessToken?: string | null },
) => {
  return axiosinstance.get(
    `bp_get_unapproved_mpesa_payments_made_information?page=${page}&limit=${limit}`,
    {
      headers: { Authorization: 'Bearer ' + String(auth?.accessToken || '') },
    },
  );
};

const makeMpesaStkPushCore = async (
  axiosinstance: AxiosInstance,
  payload: MpesaStkPushPayload,
  auth: { accessToken?: string | null },
) => {
  return axiosinstance.post(
    `bp_make_mpesa_stk_push_payment_information`,
    JSON.stringify(payload),
    {
      headers: { Authorization: 'Bearer ' + String(auth?.accessToken || '') },
    },
  );
};

const matchMpesaPaymentToOrderCore = async (
  axiosinstance: AxiosInstance,
  payload: MatchMpesaPaymentPayload,
  auth: { accessToken?: string | null },
) => {
  return axiosinstance.post(
    `bp_match_mpesa_payment_to_order_information`,
    JSON.stringify(payload),
    {
      headers: { Authorization: 'Bearer ' + String(auth?.accessToken || '') },
    },
  );
};

const getMpesaConfigurationInformationCore = async (
  axiosinstance: AxiosInstance,
  auth: { accessToken?: string | null },
) => {
  return axiosinstance.get(`/bp_employee_get_mpesa_configuration_information`, {
    headers: { Authorization: 'Bearer ' + String(auth?.accessToken || '') },
  });
};

const getMpesaStkRequestStatusCore = async (
  axiosinstance: AxiosInstance,
  stkRequestId: string,
  auth: { accessToken?: string | null },
) => {
  return axiosinstance.get<{
    status: number;
    message?: string;
    data?: StkRequestSnapshot;
  }>(`${STK_REQUEST_STATUS_ENDPOINT}/${encodeURIComponent(stkRequestId)}`, {
    headers: { Authorization: 'Bearer ' + String(auth?.accessToken || '') },
  });
};

// Public wrappers
export const getUnapprovedMpesaPayments = async (
  axiosinstance: AxiosInstance,
  page: number,
  limit: number,
  auth: { accessToken?: string | null },
) => {
  try {
    return await getUnapprovedMpesaPaymentsCore(
      axiosinstance,
      page,
      limit,
      auth,
    );
  } catch (e: unknown) {
    return Promise.reject(normalizeAxiosError(e));
  }
};

export const makeMpesaStkPush = async (
  axiosinstance: AxiosInstance,
  payload: MpesaStkPushPayload,
  auth: { accessToken?: string | null },
) => {
  try {
    return await makeMpesaStkPushCore(axiosinstance, payload, auth);
  } catch (e: unknown) {
    return Promise.reject(normalizeAxiosError(e));
  }
};

/**
 * Pays an order with the exact M-Pesa payment the cashier tapped: everything
 * still owed, or only `bp_paid_products` when given. Refusals are real HTTP
 * errors with a `code` (see utils/mpesaOrderPayment `describeMpesaMatchError`).
 */
export const matchMpesaPaymentToOrder = async (
  axiosinstance: AxiosInstance,
  payload: MatchMpesaPaymentPayload,
  auth: { accessToken?: string | null },
) => {
  try {
    return await matchMpesaPaymentToOrderCore(axiosinstance, payload, auth);
  } catch (e: unknown) {
    return Promise.reject(normalizeAxiosError(e));
  }
};

export const getMpesaConfigurationInformation = async (
  axiosinstance: AxiosInstance,
  auth: { accessToken?: string | null },
) => {
  try {
    return await getMpesaConfigurationInformationCore(axiosinstance, auth);
  } catch (e: unknown) {
    return Promise.reject(normalizeAxiosError(e));
  }
};

/** Status of one prompt, for when the till reconnects. */
export const getMpesaStkRequestStatus = async (
  axiosinstance: AxiosInstance,
  stkRequestId: string,
  auth: { accessToken?: string | null },
) => {
  try {
    return await getMpesaStkRequestStatusCore(
      axiosinstance,
      stkRequestId,
      auth,
    );
  } catch (e: unknown) {
    return Promise.reject(normalizeAxiosError(e));
  }
};

/**
 * SSE URL for one order's M-Pesa outcome (`payment:processing`,
 * `payment:completed`). Token and location travel in the query like the other
 * streams; the hook also sends them as headers.
 */
export const buildOrderPaymentStreamUrl = (
  baseUrl: string,
  orderId: string,
  accessToken: string,
  locationId: string,
) =>
  `${baseUrl.replace(/\/+$/, '')}/${ORDER_PAYMENT_STREAM_ENDPOINT}/${encodeURIComponent(
    orderId,
  )}?token=${encodeURIComponent(accessToken)}&location_id=${encodeURIComponent(
    locationId,
  )}`;
